import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4011/api';

export interface RateSessionRequest {
  rating: number;
  feedback?: string;
}

export interface Session {
  id: string;
  roomId: string;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
  rating: number | null;
  partnerRating: number | null;
  feedback: string | null;
  connectionQuality: string | null;
}

export interface SessionHistoryResponse {
  sessions: Session[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface SessionStats {
  sessionsToday: number;
  dailyLimit: number; // -1 for unlimited
  isPremium: boolean;
  sessionDurationLimit: number; // in seconds, -1 for unlimited
  remainingSessions: number; // -1 for unlimited
}

class SessionsService {
  /**
   * Rate a session by room ID
   */
  async rateSessionByRoomId(
    roomId: string,
    data: RateSessionRequest,
    token: string,
  ): Promise<{ message: string; session: Session }> {
    const response = await axios.post(
      `${API_URL}/sessions/room/${roomId}/rate`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data;
  }

  /**
   * Rate a session
   */
  async rateSession(
    sessionId: string,
    data: RateSessionRequest,
    token: string,
  ): Promise<{ message: string; session: Session }> {
    const response = await axios.post(
      `${API_URL}/sessions/${sessionId}/rate`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data;
  }

  /**
   * Get session details
   */
  async getSession(sessionId: string, token: string): Promise<Session> {
    const response = await axios.get(`${API_URL}/sessions/${sessionId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  }

  /**
   * Get user's session history
   */
  async getUserSessions(
    token: string,
    page: number = 1,
    limit: number = 20,
  ): Promise<SessionHistoryResponse> {
    const response = await axios.get(`${API_URL}/sessions`, {
      params: { page, limit },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  }

  /**
   * Get session statistics and limits
   */
  async getSessionStats(token: string): Promise<SessionStats> {
    const response = await axios.get(`${API_URL}/sessions/stats`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  }
}

export default new SessionsService();
